import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
  signal,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScrollLockService } from '../../../core/services/shared/scroll-lock.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-fullscreen-gallery',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './fullscreen-gallery.component.html',
  styleUrls: ['./fullscreen-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FullscreenGalleryComponent implements OnInit, OnDestroy {
  @Input() images: any[] = [];
  @Input() activeIndex: number = 0;
  @Output() close = new EventEmitter<void>();

  private scrollLock = inject(ScrollLockService);

  currentIndex = signal<number>(0);

  // Touch swipe tracking
  private touchStartX = 0;
  private touchEndX = 0;
  private readonly SWIPE_THRESHOLD = 50;

  ngOnInit(): void {
    this.currentIndex.set(this.activeIndex);
    this.scrollLock.lock();
  }

  ngOnDestroy(): void {
    this.scrollLock.unlock();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        this.onClose();
        break;
      case 'ArrowRight':
        this.next();
        break;
      case 'ArrowLeft':
        this.prev();
        break;
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('gallery-overlay')) {
      this.onClose();
    }
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].screenX;
    const delta = this.touchStartX - this.touchEndX;
    if (Math.abs(delta) > this.SWIPE_THRESHOLD) {
      delta > 0 ? this.next() : this.prev();
    }
  }

  next(): void {
    if (this.images.length > 0 && this.currentIndex() < this.images.length - 1) {
      this.currentIndex.set(this.currentIndex() + 1);
    }
  }

  prev(): void {
    if (this.images.length > 0 && this.currentIndex() > 0) {
      this.currentIndex.set(this.currentIndex() - 1);
    }
  }

  goTo(index: number): void {
    this.currentIndex.set(index);
  }

  onClose(): void {
    this.close.emit();
  }

  getMimeType(img: any): string {
    const url = img?.url?.toLowerCase() || '';
    if (url.endsWith('.mov')) return 'video/quicktime';
    return 'video/mp4';
  }

  downloadImage(event: MouseEvent): void {
    event.stopPropagation();
    if (typeof window === 'undefined') return;

    const img = this.images[this.currentIndex()];
    if (!img || !img.url) return;

    const url = img.url;
    const filename = url.substring(url.lastIndexOf('/') + 1) || 'yacht-photo';

    const isCrossOrigin = url.startsWith('http') && !url.includes(window.location.host);

    if (isCrossOrigin) {
      // Direct proxy download via hidden iframe to avoid opening new tabs/screens
      try {
        const proxyUrl = `${environment.apiUrl}/Boat/download-photo?url=${encodeURIComponent(url)}`;
        
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = proxyUrl;
        document.body.appendChild(iframe);
        
        // Clean up the iframe after download starts
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 5000);
      } catch (err) {
        console.error('API proxy download failed:', err);
      }
    } else {
      // Same-origin blob download (works for local files)
      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch((err) => {
          console.error('Local blob download failed:', err);
        });
    }
  }
}
